import React, { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { useRecoilState } from 'recoil';

import {
  alertsModalPageTestIDs,
  fullscreenVODPageTestIDs,
  pictureDetailedVODPageTestIDs,
  showDetailsPageHeaderTestIDs,
  Theme,
} from '@constants';
import { useContextNodeHandle } from '@hooks';
import { navigationService } from '@services';
import { screenReaderEnabledState } from '@store';

import { VOD_Icons } from '../../../assets/icons/vod';
import { isAndroid } from '../../../helpers/Platform';
import { TWCButton } from '../../focusable/TWCButton/TWCButton';
import { TWCText } from '../../primitives/TWCText/TWCText';

const tPath = 'showDetailPageHeader';

const DESCRIPTION_NUM_OF_LINES = 4;

const hideDisplay = { display: 'none' };

const getSeasonIndex = (seasons = [], currentItem = null) => {
  if (Array.isArray(seasons) && currentItem) {
    return seasons.findIndex(item => item.id === currentItem.seasonID);
  }

  return null;
};

const getEpisodeIndex = (seasons = [], currentItem = null) => {
  if (Array.isArray(seasons) && currentItem) {
    const currentSeason = seasons.find(item => item.id === currentItem.seasonID);

    if (currentSeason) {
      return currentSeason?.episodes?.findIndex(item => item.id === currentItem.id);
    }
  }
  return null;
};

export const ShowDetailsPageHeader = forwardRef(
  (
    { data, nextFocusDown, nextFocusLeft, nextFocusRight, nextFocusUp, testID, onBlur, onFocus, upNextShow },
    reference,
  ) => {
    const { t } = useTranslation('translation', { keyPrefix: tPath });

    const [screenReaderEnabled] = useRecoilState(screenReaderEnabledState);
    const isScreenFocused = useIsFocused();

    const readMoreHandle = useContextNodeHandle(pictureDetailedVODPageTestIDs.readMoreButton);
    const restartHandle = useContextNodeHandle(pictureDetailedVODPageTestIDs.restartButton);
    const playHandle = useContextNodeHandle(pictureDetailedVODPageTestIDs.headerArea);

    const readMoreNoFocus = isAndroid ? readMoreHandle : null;

    const continueWatching = upNextShow ?? null;

    const nextEpisode = continueWatching ? continueWatching : data?.seasons ? data?.seasons[0]?.episodes[0] : null;

    const nextEpisodeNumber = continueWatching?.episode ?? nextEpisode?.episode ?? 1;

    const nextSeasonNumber = continueWatching?.season ?? nextEpisode?.season ?? 1;

    const onPressReadMore = () => {
      navigationService.navigate(alertsModalPageTestIDs.alertsModalPage, {
        type: 'alertShowDescription',
        data: continueWatching ?? data,
      });
    };

    const onPressPlay = () => {
      const playlistEpisodeIndex = getEpisodeIndex(data?.seasons, nextEpisode);
      const playlistSeasonIndex = getSeasonIndex(data?.seasons, nextEpisode);

      navigationService.navigate(fullscreenVODPageTestIDs.fullscreenVODPage, {
        playlist: data?.seasons,
        playlistSeasonIndex,
        playlistEpisodeIndex,
        videoID: nextEpisode?.id,
        seasonID: nextEpisode?.seasonID,
        needsBookmarking: true,
        startPosition: nextEpisode?.point,
      });
    };

    const onPressRestart = () => {
      const playlistEpisodeIndex = getEpisodeIndex(data?.seasons, nextEpisode);
      const playlistSeasonIndex = getSeasonIndex(data?.seasons, nextEpisode);

      navigationService.navigate(fullscreenVODPageTestIDs.fullscreenVODPage, {
        playlist: data?.seasons,
        playlistSeasonIndex,
        playlistEpisodeIndex,
        videoID: nextEpisode?.id,
        seasonID: nextEpisode?.seasonID,
        needsBookmarking: true,
      });
    };

    return (
      <View
        accessibilityLabel={t('accessibilityLabel', {
          title: data?.title,
          year: data?.year,
          seasons: data?.seasons?.length,
          rating: data?.rating,
          description: data?.description,
        })}
        accessibilityRole={'text'}
        accessible={screenReaderEnabled}
        ref={reference}
        style={styles.container}
        testID={showDetailsPageHeaderTestIDs.header}
      >
        <>
          <View style={styles.header} />
          <View style={styles.subHeader}>
            <TWCText testID={showDetailsPageHeaderTestIDs.yearText} variant={'regularText30Transparent'}>
              {data?.year}
            </TWCText>
            <View style={styles.divider} />
            <TWCText testID={showDetailsPageHeaderTestIDs.seasonsText} variant={'regularText30Transparent'}>
              {data?.seasons?.length}
              {' Seasons'}
            </TWCText>
            <View style={styles.divider} />
            <TWCText testID={showDetailsPageHeaderTestIDs.ratingText} variant={'regularText30Transparent'}>
              {data?.rating}
            </TWCText>
          </View>
          <View style={styles.middle}>
            {continueWatching?.title && (
              <TWCText
                numberOfLines={1}
                style={styles.subtitle}
                testID={showDetailsPageHeaderTestIDs.descriptionText}
                variant={'regularText42'}
              >
                {`S${nextSeasonNumber} `}
                {`E${nextEpisodeNumber} `}
                {continueWatching?.title}
              </TWCText>
            )}
            <TWCText
              ellipsizeMode={'tail'}
              numberOfLines={DESCRIPTION_NUM_OF_LINES}
              style={styles.description}
              testID={showDetailsPageHeaderTestIDs.descriptionText}
              variant={'regularText30Transparent'}
            >
              {continueWatching ? continueWatching?.description : data?.description}
            </TWCText>
          </View>
          <TWCButton
            accessibilityHint={t('readMoreButtonAccessibilityHint')}
            accessibilityLabel={
              isAndroid
                ? t('btnReadMore')
                : t('accessibilityLabel', {
                    title: data?.title,
                    year: data?.year,
                    seasons: data?.seasons?.length,
                    rating: data?.rating,
                    description: data?.description,
                  })
            }
            height={66}
            nextFocusDown={isScreenFocused ? nextFocusLeft : null}
            nextFocusLeft={isScreenFocused ? readMoreNoFocus : null}
            nextFocusRight={isScreenFocused ? readMoreNoFocus : null}
            nextFocusUp={isScreenFocused ? readMoreNoFocus : null}
            testID={pictureDetailedVODPageTestIDs.readMoreButton}
            textFocusStyle={styles.btnReadMoreFocus}
            textStyle={styles.btnReadMore}
            title={t('btnReadMore')}
            width={212}
            onBlur={onBlur}
            onFocus={onFocus}
            onPress={onPressReadMore}
          />
          <View style={styles.btnWrapper}>
            <TWCButton
              accessibilityHint={t(continueWatching ? 'resumeButtonAccessibilityHint' : 'playButtonAccessibilityHint')}
              accessibilityLabel={t(
                continueWatching ? 'resumeButtonAccessibilityLabel' : 'playButtonAccessibilityLabel',
                {
                  series: data?.title ?? '',
                  season: nextSeasonNumber,
                  episode: nextEpisodeNumber,
                  title: continueWatching?.title ?? '',
                },
              )}
              icon={VOD_Icons.play}
              iconFocused={VOD_Icons.playFocused}
              nextFocusDown={nextFocusDown}
              nextFocusLeft={isAndroid ? playHandle : null}
              nextFocusRight={continueWatching ? nextFocusRight : isAndroid ? playHandle : null}
              nextFocusUp={nextFocusUp}
              progress={continueWatching?.progress}
              testID={testID}
              title={t(continueWatching?.progress ? 'btnResume' : 'btnPlay', {
                season: nextSeasonNumber,
                episode: nextEpisodeNumber,
              })}
              onBlur={onBlur}
              onFocus={onFocus}
              onPress={onPressPlay}
            />
            <View style={[continueWatching?.progress ? null : hideDisplay]}>
              <TWCButton
                accessibilityHint={t('restartButtonAccessibilityHint')}
                accessibilityLabel={t('restartButtonAccessibilityLabel')}
                icon={VOD_Icons.reload}
                iconFocused={VOD_Icons.reloadFocused}
                nextFocusDown={nextFocusDown}
                nextFocusLeft={nextFocusLeft}
                nextFocusRight={isAndroid ? restartHandle : null}
                nextFocusUp={nextFocusUp}
                style={styles.btnRestart}
                testID={pictureDetailedVODPageTestIDs.restartButton}
                title={t('btnRestart')}
                onBlur={onBlur}
                onFocus={onFocus}
                onPress={onPressRestart}
              />
            </View>
          </View>
        </>
      </View>
    );
  },
);

const styles = StyleSheet.create({
  btnReadMore: {
    fontSize: Theme.px(33),
  },
  btnReadMoreFocus: {
    color: Theme.switch.focusColor,
    fontSize: Theme.px(33),
  },
  btnRestart: {
    marginLeft: Theme.px(12),
  },
  btnWrapper: {
    flexDirection: 'row',
    paddingTop: Theme.px(70),
  },
  container: {
    elevation: 10,
    flexDirection: 'column',
    width: Theme.px(837),
    zIndex: 9999,
  },
  description: {
    lineHeight: Theme.px(42),
  },
  divider: {
    height: Theme.px(28),
    width: Theme.px(36),
  },
  header: {
    alignItems: 'center',
    flexDirection: 'row',
    height: Theme.px(87),
    justifyContent: 'flex-start',
    width: '100%',
  },
  middle: {
    paddingVertical: Theme.px(42),
  },
  subHeader: {
    flexDirection: 'row',
  },
  subtitle: {
    marginBottom: Theme.px(10),
  },
});
